import { ReactElement, useMemo, useState } from 'react';
import clsx from 'clsx';
import Image from 'next/image';
import { NumberStepper } from '@appsumo/dorado-react';
import { toast } from 'react-hot-toast';

import { CartItemProps, CartCouponProps, UseCartProps } from '~/types/cart';
import { CouponType } from '~/constants/cart';
import { getCurrentPlan } from '~/lib/cart/util';
import { useEvent } from '~/lib/events';

import { DealActions, DealPlanType, DealPrice } from '~/components/cart';
import DealCouponLabel from './DealCouponLabel';

const THUMBNAIL_WIDTH = 48;
const THUMBNAIL_HEIGHT = 27;

export default function DealItem({
  item,
  hasPlus,
  isSubscriptionDeal = false,
  couponData,
  lineItemUpdate,
}: {
  item: CartItemProps;
  hasPlus: boolean;
  isSubscriptionDeal?: boolean;
  couponData?: CartCouponProps;
  lineItemUpdate?: UseCartProps['lineItemUpdate'];
}): ReactElement {
  const [quantity, setQuantity] = useState(item?.quantity);

  // Update the quantity once the upsell plan is added to the cart
  useEvent(
    'cart:update_code_quantity',
    ({ value, dealId }: { value: number; dealId: number }) => {
      if (value > 0 && dealId === item.deal.id) {
        setQuantity(value);
      }
    },
  );

  const updateQuantity = async (val: number) => {
    if (val === quantity || !lineItemUpdate) return;

    const response = await lineItemUpdate(item.deal.id, val);

    // [2] is the response from the API ([0] is user mutate and [1] is cart mutate)
    if (response?.[2]?.success) {
      setQuantity(val);
    } else {
      toast.error('Sorry, unable to update the quantity, please try again.');
    }
  };

  const currentPlan = useMemo(() => {
    return getCurrentPlan(item?.deal_plan, item?.deal.plans);
  }, [item?.deal_plan, item?.deal?.plans]);

  if (!item?.deal) return <></>;

  // - Show the green label coupon if there is a coupon used and this item is NOT discounted but has plus
  //   or if this item is discounted and does or does not have plus
  // - Show red label if all items are plus but no coupon used
  // - Show black price if no coupon or plus discount is allowed
  const isFixedCoupon =
    couponData?.coupon_id && couponData?.type === CouponType.FIXED;
  const hasPlusDiscount = hasPlus && item.plan_discount > 0;
  let isItemDiscounted =
    !!couponData?.coupon_id &&
    item.subtotal > 0 &&
    (item.coupon_discount > 0 || hasPlusDiscount);
  let couponLabel =
    (!!couponData?.coupon_id &&
      item.subtotal > 0 &&
      item.coupon_discount &&
      couponData?.description) ||
    null;
  let subtotalAfterDiscounts = item.post_discount_total;

  if (isFixedCoupon) {
    // We do not want to show the discount for fixed coupons since the calc can be confusing to the user
    subtotalAfterDiscounts = item.subtotal - (item.plan_discount || 0);
    isItemDiscounted = false;
    couponLabel = null;
  }

  return (
    <>
      <div
        className={clsx(
          'mb-4 grid grid-cols-[66%_34%] items-center gap-y-4 lg:mb-2 lg:gap-y-0 lg:gap-x-6',
          isSubscriptionDeal
            ? 'mt-4 lg:grid-cols-[1fr_1fr]'
            : 'lg:grid-cols-[minmax(200px,_1fr)_minmax(200px,_1fr)_100px_90px]',
        )}
      >
        <div className="order-1 lg:order-none lg:mb-0">
          <div className="flex items-center">
            <Image
              src={`${item.deal.image}?width=${THUMBNAIL_WIDTH}&height=${THUMBNAIL_HEIGHT}&aspect_ratio=16:9`}
              unoptimized
              width={THUMBNAIL_WIDTH}
              height={THUMBNAIL_HEIGHT}
              alt={item.deal.public_name}
              className="mr-2 inline aspect-sku-card rounded object-cover"
            />
            <div className="font-medium line-clamp-2 sm:line-clamp-1 lg:line-clamp-2">
              {item.deal.public_name}
            </div>
          </div>
          {couponLabel && (
            <div
              className="mt-2 flex lg:mt-0 lg:hidden"
              data-testid="deal-item-coupon-label"
            >
              <DealCouponLabel couponLabel={couponLabel} />
            </div>
          )}
        </div>

        {!isSubscriptionDeal && (
          <>
            <div className="order-3 mr-4 lg:order-none lg:mr-0 lg:mb-0">
              <DealPlanType
                dealId={item.deal.id}
                currentPlan={currentPlan}
                planCategory={item.deal_plan.plan_category}
                plans={item.deal.plans}
                isLicensing={item.deal.use_licensing}
                helpText={item.deal_plan.plan_type_help_text}
              />
            </div>
            <div
              className="order-4 justify-self-end lg:order-none lg:mb-0 lg:justify-self-auto"
              data-testid="deal-item-stepper"
            >
              <NumberStepper
                value={quantity}
                min={1}
                max={item.deal.user_quantity_limit ?? 1}
                onChange={updateQuantity}
                className="h-[42px]" // To match min height of dropdown
              />
            </div>
          </>
        )}
        <div className="order-2 text-right font-medium last:self-start last:justify-self-end lg:order-none lg:mb-0 lg:last:self-auto">
          <DealPrice
            subtotal={item.subtotal}
            hasPlus={hasPlusDiscount}
            hasCoupon={isItemDiscounted}
            subtotalAfterDiscounts={subtotalAfterDiscounts}
            isSubscriptionDeal={isSubscriptionDeal}
          />
        </div>
      </div>
      <div>
        <DealActions dealId={item.deal.id} couponLabel={couponLabel} />
      </div>
    </>
  );
}

export { DealItem };
